/* eslint-disable no-param-reassign */
import { TokensDto } from '@api/apiCompat';
import { invalidateAllTags } from '@api/baseApi';
import { USER_ACCESS_TOKEN, USER_REFRESH_TOKEN } from '@constants/auth';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tokenStorage } from '@utils/tokenStorage';

interface AuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  forgotPasswordEmail: string;
  isConfirmed: boolean | null;
  authResponse?: TokensDto | null;
  accessToken?: string;
  refreshToken?: string;
}

const initialState: AuthState = {
  isAuthenticated: !!tokenStorage.getToken(USER_ACCESS_TOKEN),
  isAdmin: tokenStorage.getToken('user_is_admin') === 'true',
  forgotPasswordEmail: '',
  isConfirmed: null,
  authResponse: null,
  accessToken: tokenStorage.getToken(USER_ACCESS_TOKEN) || undefined,
  refreshToken: tokenStorage.getToken(USER_REFRESH_TOKEN) || undefined,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess(state: AuthState, action: PayloadAction<TokensDto>) {
      invalidateAllTags();
      state.authResponse = action.payload;
      state.isAuthenticated = true;
      state.isConfirmed = null;
      // Store tokens if they exist in the response
      if ((action.payload as any).access) {
        state.accessToken = (action.payload as any).access;
        tokenStorage.setToken(USER_ACCESS_TOKEN, (action.payload as any).access);
      }
      if ((action.payload as any).refresh) {
        state.refreshToken = (action.payload as any).refresh;
        tokenStorage.setToken(USER_REFRESH_TOKEN, (action.payload as any).refresh);
      }
      // Store admin flag from login response
      const isAdmin = (action.payload as any).is_admin === true;
      state.isAdmin = isAdmin;
      tokenStorage.setToken('user_is_admin', isAdmin ? 'true' : 'false');
    },
    logoutSuccess(state: AuthState) {
      state.authResponse = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.isConfirmed = null;
      // Clear admin flag from storage on logout
      tokenStorage.removeToken('user_is_admin');
    },
    provideForgotPasswordEmail(state: AuthState, action: PayloadAction<string>) {
      state.forgotPasswordEmail = action.payload;
    },
    clearForgotPasswordEmail(state: AuthState) {
      state.forgotPasswordEmail = '';
    },
    confirmSuccess(state: AuthState) {
      state.isConfirmed = true;
    },
    clearConfirm(state: AuthState) {
      state.isConfirmed = false;
    },
  },
});

export const {
  loginSuccess,
  logoutSuccess,
  provideForgotPasswordEmail,
  clearForgotPasswordEmail,
  confirmSuccess,
  clearConfirm,
} = authSlice.actions;
export default authSlice.reducer;
